<template>
  <div>
    <v-sheet elevation="8">
      <v-alert
        v-model="showDirectionsAlert"
        closable
        type="info"
        variant="tonal"
        border="start"
        class="ma-2 mb-2"
        @update:model-value="onDirectionsAlertChange"
      >
        Cliquez sur les icônes <b>put in</b> et <b>take out</b> pour être redirigé vers l'application Google Maps pour
        les directions.
      </v-alert>
      <google-map
        v-if="mapsAvailable"
        ref="sectionMaps"
        :api-promise="mapsApiPromise"
        :center="mapOptions.center"
        :zoom="mapOptions.zoom"
        :map-type-id="mapOptions.mapTypeId"
        style="height: 50vh"
        @zoom_changed="onMapZoomChanged"
      >
        <map-marker
          v-for="(marker, markerIndex) in markers"
          :key="`marker-${markerIndex}`"
          :options="marker.options"
          @click="openGoogleMap(marker)"
        />
        <map-circle
          v-for="(circle, circleIndex) in accuracyCircles"
          :key="`accuracy-circle-${circleIndex}`"
          :options="circle"
        />
        <map-polyline :options="{ path: sectionPath, editable, strokeColor: '#1F8FA6', strokeWeight: 5 }" />
        <map-polyline
          v-if="directionsPolyline.length > 0"
          :options="{ path: directionsPolyline, strokeColor: '#0B3C5D', strokeWeight: 3, strokeOpacity: 0.7 }"
        />
      </google-map>
    </v-sheet>

    <v-sheet v-if="directionsSteps.length > 0" elevation="4" class="mt-4 pa-4">
      <v-row>
        <v-col>
          <h3>Itinéraire ({{ directionsDistance }} - {{ directionsTime }})</h3>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-list density="compact">
            <v-list-item v-for="(step, index) in directionsSteps" :key="index">
                <v-list-item-title>
                  <span class="font-weight-bold">{{ index + 1 }}.</span>
                  <span v-html="step.instructions"></span>
                  <span class="ml-2 text-body-small text-medium-emphasis">({{ step.distance }})</span>
                </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-col>
      </v-row>
    </v-sheet>

    <v-alert v-if="directionsErrorMessage" type="warning" variant="outlined" density="compact" class="mt-4">
      {{ directionsErrorMessage }}
    </v-alert>
  </div>
</template>

<script>
  import SectionsService from "@/store/modules/sections";
  import { GoogleMap, Marker as MapMarker, Polyline as MapPolyline, Circle as MapCircle } from "vue3-google-map";
  import { GmapsService } from "@/services/Gmaps.service";
  import { isGoogleMapsAvailable, loadGoogleMaps } from "@/services/GoogleMapsLoader.service";
  export default {
    components: { GoogleMap, MapMarker, MapPolyline, MapCircle },
    props: {
      section: {
        type: Object,
        default: function () {
          return {};
        },
      },
    },
    data() {
      return {
        mapsAvailable: isGoogleMapsAvailable(),
        mapsApiPromise: isGoogleMapsAvailable() ? loadGoogleMaps() : null,
        directionsAlertStorageKey: "qww-directions-alert-dismissed",
        showDirectionsAlert: true,
        gmaps_key: process.env.VUE_APP_GOOGLE_MAPS_KEY,
        mapOptions: {
          mapTypeId: "roadmap",
          center: GmapsService.BASE_CENTER_POSITION,
          zoom: 12,
        },
        markers: [],
        sectionPath: [],
        editable: false,
        directionsSteps: [],
        directionsPolyline: [],
        directionsDistance: "",
        directionsTime: "",
        directionsErrorMessage: "",
        disableEmbeddedDirections: false,
        currentMapZoom: 12,
      };
    },
    computed: {
      accuracyCircles() {
        const zoom = Number(this.currentMapZoom) || 12;
        const radius = zoom <= 11 ? 45 : zoom <= 13 ? 30 : 18;

        return this.markers
          .filter((marker) => marker && marker.options && marker.options.position)
          .map((marker) => ({
            center: marker.options.position,
            radius,
            strokeColor: "#0B3C5D",
            strokeOpacity: 0.45,
            strokeWeight: 1,
            fillColor: marker.type === "putin" ? "#1F8FA6" : "#0B3C5D",
            fillOpacity: 0.08,
          }));
      },
    },
    async created() {
      this.loadDirectionsAlertPreference();
      await this.getDirections();
    },
    mounted() {
      if (typeof window !== "undefined") {
        window.addEventListener("resize", this.handleViewportChange, { passive: true });
      }
    },
    beforeUnmount() {
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", this.handleViewportChange);
      }
    },
    methods: {
      // Marker styles use Maps classes (google.maps.Point), which exist only once the API has loaded.
      async waitForMapsApi() {
        if (!this.mapsApiPromise) return;
        try {
          await this.mapsApiPromise;
        } catch (error) {
          console.error("Unable to load Google Maps:", error);
        }
      },
      handleViewportChange() {
        if (this.markers.length > 0) this.setCenterPosition();
      },
      onMapZoomChanged() {
        const map = this.$refs.sectionMaps && this.$refs.sectionMaps.map;
        this.currentMapZoom = (map && Number(map.getZoom())) || this.currentMapZoom;
        this.applyMarkerVisualStyles();
      },
      getMapViewportSize() {
        let width = 0;
        let height = 0;

        if (this.$refs.sectionMaps && this.$refs.sectionMaps.$el) {
          width = this.$refs.sectionMaps.$el.clientWidth || 0;
          height = this.$refs.sectionMaps.$el.clientHeight || 0;
        }

        if (typeof window !== "undefined") {
          width = width || window.innerWidth || 640;
          height = height || Math.round((window.innerHeight || 600) * 0.6);
        }

        return { width, height };
      },
      loadDirectionsAlertPreference() {
        if (typeof window === "undefined") return;

        const isDismissed = window.localStorage.getItem(this.directionsAlertStorageKey) === "true";
        this.showDirectionsAlert = !isDismissed;
      },
      onDirectionsAlertChange(isVisible) {
        if (typeof window === "undefined") return;

        if (!isVisible) {
          window.localStorage.setItem(this.directionsAlertStorageKey, "true");
        }
      },
      openGoogleMap(marker) {
        const urlSuffix = marker.options.position.lat + "," + marker.options.position.lng;
        const zoom = ",16z";

        window.open("https://www.google.com/maps/dir//" + urlSuffix + "/@" + urlSuffix + zoom, "_blank");
      },
      markerActions() {
        console.log("Actions!!!");
      },
      async getDirections() {
        this.directions = await SectionsService.actions.findSectionsDirections(this.section.id);
        await this.waitForMapsApi();
        this.setMarkers();
        this.setSectionPath();
      },
      setSectionPath() {
        this.sectionPath = [];
        if (this.directions.sectionPath) {
          if (this.directions.sectionPath.length > 0) this.sectionPath = JSON.parse(this.directions.sectionPath);
        }
      },
      setMarkers() {
        this.markers = [];

        if (!this.directions.markers) {
          this.getOldMarkers();
        } else if (this.directions.markers.length == 0) {
          this.getOldMarkers();
        } else {
          this.markers = JSON.parse(this.directions.markers);
        }

        this.applyMarkerVisualStyles();
        this.setCenterPosition();
        this.fetchTravelDirections();
      },
      setCenterPosition() {
        if (this.markers.length > 0) {
          const putInMarker = this.markers.find((marker) => marker.type === "putin");
          const takeOutMarker = this.markers.find((marker) => marker.type === "takeout");
          const fallbackPositions = this.markers.map((marker) => marker.options.position);
          const { width, height } = this.getMapViewportSize();

          const positionA = putInMarker
            ? `${putInMarker.options.position.lat},${putInMarker.options.position.lng}`
            : fallbackPositions[0]
            ? `${fallbackPositions[0].lat},${fallbackPositions[0].lng}`
            : "";
          const positionB = takeOutMarker
            ? `${takeOutMarker.options.position.lat},${takeOutMarker.options.position.lng}`
            : fallbackPositions[1]
            ? `${fallbackPositions[1].lat},${fallbackPositions[1].lng}`
            : "";
          const { center, zoom } = GmapsService.getCenterPosition(positionA, positionB, {
            viewportWidth: width,
            viewportHeight: height,
          });
          this.mapOptions.center = center;
          this.mapOptions.zoom = zoom;
        } else {
          this.mapOptions.center = GmapsService.BASE_CENTER_POSITION;
          this.mapOptions.zoom = 10;
        }
      },
      getOldMarkers() {
        if (GmapsService.getLatLngFromStringLatLng(this.directions.takeout))
          this.markers.push({
            type: "putin",
            options: {
              position: GmapsService.getLatLngFromStringLatLng(this.directions.takeout),
              label: GmapsService.LABEL_PUTIN,
              icon: GmapsService.SVG_PUTIN,
            },
          });

        if (this.directions.putin.length > 0)
          if (GmapsService.getLatLngFromStringLatLng(this.directions.putin))
            this.markers.push({
              type: "takeout",
              options: {
                position: GmapsService.getLatLngFromStringLatLng(this.directions.putin),
                label: GmapsService.LABEL_TAKEOUT,
                icon: GmapsService.SVG_TAKEOUT,
              },
            });
      },
      getSVGsLabelOrigin() {
        var point = new window.google.maps.Point(12, 30);
        var svgPutin = GmapsService.SVG_PUTIN;
        var svgTakeout = GmapsService.SVG_TAKEOUT;
        svgPutin.labelOrigin = point;
        svgTakeout.labelOrigin = point;

        return { putin: svgPutin, takeout: svgTakeout };
      },
      applyMarkerVisualStyles() {
        if (typeof window === "undefined" || !window.google || !window.google.maps || !window.google.maps.Point) return;

        const zoom = Number(this.currentMapZoom) || this.mapOptions.zoom || 12;
        const size = zoom <= 9 ? 36 : zoom <= 11 ? 40 : zoom <= 13 ? 44 : 48;
        const shortLabelMode = zoom <= 10;
        const { Point, Size } = window.google.maps;

        this.markers = this.markers.map((marker) => {
          if (!marker || !marker.options) return marker;

          const isPutIn = marker.type === "putin";
          const baseLabel = isPutIn ? GmapsService.LABEL_PUTIN : GmapsService.LABEL_TAKEOUT;

          // Brand pin (Eau put-in, Marine take-out). The pin tip is at 112/120 of its height: anchored there so
          // the coordinate stays exact, with the label under it.
          const icon = {
            url: GmapsService.brandPinUrl(marker.type),
            scaledSize: new Size(size, size),
            anchor: new Point(size / 2, (size * 112) / 120),
            labelOrigin: new Point(size / 2, size + 8),
          };

          const label = {
            ...baseLabel,
            text: shortLabelMode ? (isPutIn ? "P" : "T") : baseLabel.text,
            color: "#0B3C5D",
            fontWeight: "800",
            className: "map-pin-label text-label-small text-uppercase",
          };

          return {
            ...marker,
            options: {
              ...marker.options,
              icon,
              label,
            },
          };
        });
      },
      decodeEncodedPolyline(encoded) {
        if (!encoded || typeof encoded !== "string") return [];

        let index = 0;
        const len = encoded.length;
        let lat = 0;
        let lng = 0;
        const path = [];

        while (index < len) {
          let shift = 0;
          let result = 0;
          let byte = null;

          do {
            byte = encoded.charCodeAt(index++) - 63;
            result |= (byte & 0x1f) << shift;
            shift += 5;
          } while (byte >= 0x20);

          const dLat = result & 1 ? ~(result >> 1) : result >> 1;
          lat += dLat;

          shift = 0;
          result = 0;

          do {
            byte = encoded.charCodeAt(index++) - 63;
            result |= (byte & 0x1f) << shift;
            shift += 5;
          } while (byte >= 0x20);

          const dLng = result & 1 ? ~(result >> 1) : result >> 1;
          lng += dLng;

          path.push({
            lat: lat / 1e5,
            lng: lng / 1e5,
          });
        }

        return path;
      },
      formatDurationFromSeconds(valueInSeconds) {
        const totalSeconds = Number(valueInSeconds) || 0;
        const totalMinutes = Math.round(totalSeconds / 60);

        if (totalMinutes < 60) return `${totalMinutes} min`;

        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        if (minutes === 0) return `${hours} h`;
        return `${hours} h ${minutes} min`;
      },
      formatDistanceFromMeters(distanceInMeters) {
        const meters = Number(distanceInMeters) || 0;
        if (meters < 1000) return `${meters} m`;
        return `${(meters / 1000).toFixed(1)} km`;
      },
      parseDurationStringToSeconds(durationValue) {
        if (!durationValue || typeof durationValue !== "string") return 0;
        const sanitizedDuration = durationValue.endsWith("s") ? durationValue.slice(0, -1) : durationValue;
        return Number(sanitizedDuration) || 0;
      },
      async fetchTravelDirections() {
        if (this.disableEmbeddedDirections) return;

        const putInMarker = this.markers.find((marker) => marker.type === "putin");
        const takeOutMarker = this.markers.find((marker) => marker.type === "takeout");

        if (!putInMarker || !takeOutMarker) {
          this.directionsSteps = [];
          this.directionsPolyline = [];
          this.directionsDistance = "";
          this.directionsTime = "";
          this.directionsErrorMessage = "";
          return;
        }

        try {
          this.directionsErrorMessage = "";
          const mapsKey = process.env.VUE_APP_GOOGLE_MAPS_KEY;
          if (!mapsKey) throw new Error("MISSING_MAPS_KEY");

          const response = await fetch("https://routes.googleapis.com/directions/v2:computeRoutes", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Goog-Api-Key": mapsKey,
              "X-Goog-FieldMask":
                "routes.duration,routes.distanceMeters,routes.polyline.encodedPolyline,routes.legs.steps.navigationInstruction.instructions,routes.legs.steps.distanceMeters",
            },
            body: JSON.stringify({
              origin: {
                location: {
                  latLng: {
                    latitude: putInMarker.options.position.lat,
                    longitude: putInMarker.options.position.lng,
                  },
                },
              },
              destination: {
                location: {
                  latLng: {
                    latitude: takeOutMarker.options.position.lat,
                    longitude: takeOutMarker.options.position.lng,
                  },
                },
              },
              travelMode: "DRIVE",
              routingPreference: "TRAFFIC_UNAWARE",
              languageCode: "fr-CA",
              units: "METRIC",
              polylineQuality: "OVERVIEW",
            }),
          });

          const result = await response.json();
          if (!response.ok || !result.routes || result.routes.length === 0) {
            throw new Error(result.error && result.error.message ? result.error.message : "ROUTES_API_REQUEST_FAILED");
          }

          const route = result.routes[0];
          const leg = route.legs && route.legs.length > 0 ? route.legs[0] : null;
          if (!leg) throw new Error("ROUTE_LEG_NOT_FOUND");

          this.directionsDistance = this.formatDistanceFromMeters(route.distanceMeters);
          this.directionsTime = this.formatDurationFromSeconds(this.parseDurationStringToSeconds(route.duration));

          this.directionsSteps = (leg.steps || []).map((step) => ({
            instructions: step.navigationInstruction ? step.navigationInstruction.instructions : "Continuer",
            distance: this.formatDistanceFromMeters(step.distanceMeters),
          }));

          this.directionsPolyline = this.decodeEncodedPolyline(route.polyline ? route.polyline.encodedPolyline : "");
        } catch (error) {
          this.directionsSteps = [];
          this.directionsPolyline = [];
          this.directionsDistance = "";
          this.directionsTime = "";

          const errorMessage = error && error.message ? error.message : "";
          const isRequestDenied =
            errorMessage.includes("REQUEST_DENIED") ||
            errorMessage.includes("LegacyApiNotActivated") ||
            errorMessage.includes("API key not valid") ||
            errorMessage.includes("PERMISSION_DENIED");

          if (isRequestDenied) {
            this.disableEmbeddedDirections = true;
            this.directionsErrorMessage =
              "Les directions intégrées ne sont pas disponibles avec la configuration actuelle de la clé Google Maps. " +
              "Utilisez les icônes put in/take out pour ouvrir l'itinéraire dans Google Maps.";
          } else {
            this.directionsErrorMessage = "Impossible de charger l'itinéraire intégré pour le moment.";
            console.error("Error fetching directions:", error);
          }
        }
      },
    },
  };
</script>

<style lang="scss" scoped></style>
